import { reduce, find, first, filter as lodashFilter, sortBy, each,
  last, isArray, findIndex, tail, concat, pick, cloneDeep } from 'lodash';
import moment from 'moment';
import objectHash from 'object-hash';

import { LOAD_SEARCH_FORM, UPDATE_CRITERIA, DELETE_CRITERIA,
  ADD_CRITERIA, LOAD_CRITERIA_FILTER_DETAIL, SUBMIT_SEARCH, VIEW_SAVED_SEARCH,
  RESET_DEFAULT_SEARCH, CHECK_DIRTY,
  ADD_SAVED_SEARCH, UPDATE_SAVED_SEARCH, DELETE_SAVED_SEARCH } from './search-form-actions';

const empty = {
  filters: [],
  criteria: [],
  objects: {},
  savedSearches: [],
  savedSearch: null, // selected saved search
  defaultQuery: {},
  statsConditions: {}, // stats for filters amount, close date, etc, ... eg: { is_closed: true }
  query: {
    _id: 'ROOT', // root query
    type: 'groupAND',
    values: [],
  },
  minCloseDate: null, // https://github.com/lumen-software/TruSales/issues/593
  maxCloseDate: null,
  id: null,
  status: null,
  errors: null,
  isDirty: false,
};

const initialState = {
  hasNoDecisionSetup: false,
  forecastCategoryFilter: null,
  closeDateFilter: null,
  hasDealTypeSetup: false,
  accountIndustryFilter: null,
  // isolate by search type (`outcome`, `dashboard`, `performance`, etc...)
  searches: {

  },
};

function findAndInsert(tree, match = {}, item) {
  if (tree.type === 'groupAND' || tree.type === 'groupOR') {
    const root = find([tree], match);
    if (root) {
      const newRoot = { ...tree, values: concat(tree.values, item) };
      return newRoot;
    }
    return {
      ...tree,
      values: reduce(tree.values, (items, value) => concat(items, findAndInsert(value, match, item)), []),
    };
  }
  return tree;
}


function removeItemFromPath(node, ids) {
  const id = first(ids);
  if (node._id === id) {
    if (ids.length === 1) {
      return null;
    }
    const restOfIds = tail(ids);
    return {
      ...node,
      values: reduce(node.values, (items, value) => {
        const r = removeItemFromPath(value, restOfIds);
        if (r) {
          items.push(r);
        }
        return items;
      }, []),
    };
  }
  return node;
}

function updateItemFromPath(node, ids, params) {
  const id = first(ids);
  if (node._id === id) {
    if (ids.length === 1) {

      if (params.type && params.type !== 'groupAND' && params.type !== 'groupOR' && node.type !== params.type) {
        if (params.type === 'set') {
          const newValues = [true];
          const newMappings = [];
          return { ...node, ...params, values: newValues, mappings: newMappings };  
        }
        if (node.type === 'set') {
          // previsous is `set`
          return { ...node, ...params, values: [], mappings: [] };
        }
      }
      return { ...node, ...params };
    }
    if (node.type === 'groupAND' || node.type === 'groupOR') {
      const restOfIds = tail(ids);
      return {
        ...node,
        values: reduce(node.values, (items, value) => {
          const r = updateItemFromPath(value, restOfIds, params);
          items.push(r);
          return items;
        }, []),
      };
    }
  }
  return node;
}

function addFilterQuery(criterion, filters) {
  if (criterion) {
    if (criterion.type === 'groupAND' || criterion.type === 'groupOR') {
      return {
        ...criterion,
        values: reduce(criterion.values, (items, val) => {
          const newVal = addFilterQuery(val, filters);
          if (newVal) {
            items.push(newVal);
          }
          return items;
        }, []),
      };
    }
    if (!criterion.filter_id) {
      criterion.filter_id = criterion._id;
    }
    const f = find(filters, { _id: criterion.filter_id });
    if (f) {
      return { ...criterion, filter: f };
    }
  }
  return null;
}

export const cleanSubmitCriteria = (criteria) => {
  if (criteria) {
    if (criteria.type === 'groupAND' || criteria.type === 'groupOR') {
      return {
        ...(pick(criteria, ['type', 'id'])),
        values: reduce(criteria.values, (items, val) => items.concat(cleanSubmitCriteria(val)), []),
      };
    }
    return {
      values: [],
      mappings: [],
      ...pick(criteria, ['_id', 'type', 'values', 'mappings', 'control_type', 'filter_id']),
    };
  }
  return criteria;
};

function checkDirty(query, defaultQuery) {
  const one = cleanSubmitCriteria(query);
  const two = cleanSubmitCriteria(defaultQuery);
  if (one && two && (one.values || []).length === 0 && (two.values || []).length === 0 && (one.mappings || []).length === 0 && (two.mappings || []).length === 0) {
    return false; // empty, not dirty
  }
  return objectHash(one, { unorderedArrays: true }) !== objectHash(two, { unorderedArrays: true });
}


const SearchFormReducer = (state = initialState, action) => {
  switch (action.type) {
    case LOAD_SEARCH_FORM: {
      const {
        savedSearch, savedSearches, statsConditions, query, defaultQuery, objects, id, status, minDate, maxDate,
        searchType, isDynamic, dynamicFilters,
      } = action;

      const filters = [...action.filters]
      if (isDynamic) {
        each(dynamicFilters, (dynamicFilter) => {
          if (!find(filters, { _id: dynamicFilter._id })) {
            filters.push(dynamicFilter);
          }
        });
      }

      let newQuery = addFilterQuery(query, filters);
      if (!newQuery) {
        newQuery = {
          _id: 'ROOT',
          type: 'groupAND',
          values: [],
        };
      }
      const newDefaultQuery = addFilterQuery(defaultQuery || {}, filters);

      const minCloseDate = minDate ? moment(minDate, 'YYYY-MM-DD') : null;
      const maxCloseDate = maxDate ? moment(maxDate, 'YYYY-MM-DD') : null;

      const accountIndustryFilter = find(filters, { tag: 'accountIndustry' });
      const forecastCategoryFilter = find(filters, { tag: 'forecast' });
      const closeDateFilter = find(filters, { tag: 'closeDate' });
      const dealTypeFilter = find(filters, { tag: 'dealType' });
      const stageFilter = find(filters, { tag: 'stage' });
      const dealType = dealTypeFilter ? find(dealTypeFilter.mappings, item => item.values && item.values.length > 0) : null;
      const hasDealTypeSetup = dealType !== undefined;
      const ndStage = stageFilter ? find(stageFilter.mappings, item => item.label === 'No Decision' && item.values && item.values.length > 0) : null;
      const hasNoDecisionSetup = ndStage !== undefined;

      return {
        ...state,
        hasNoDecisionSetup,
        hasDealTypeSetup,
        forecastCategoryFilter,
        closeDateFilter,
        accountIndustryFilter,
        searches: {
          ...state.searches,
          [searchType]: {
            query: newQuery,
            objects: objects || {},
            minCloseDate,
            maxCloseDate,
            statsConditions,
            defaultQuery: newDefaultQuery,
            savedSearches,
            savedSearch,
            id,
            filters: filters || [],
            status,
            isDirty: checkDirty(newQuery, newDefaultQuery),
          },
        },
      };
    }
    case CHECK_DIRTY: {
      const { searchType } = action;
      const c = state.searches[searchType] || {};
      return {
        ...state,
        searches: {
          ...state.searches,
          [searchType]: {
            ...state.searches[searchType],
            isDirty: checkDirty(c.query || {}, c.defaultQuery || {}),
          },
        },
      };
    }
    case SUBMIT_SEARCH: {
      const {
        criteria, id, minDate, maxDate, searchType,
      } = action;
      const { searches } = state;
      const { filters } = searches[searchType] || {};

      const newCriteria = reduce(criteria, (items, criterion) => {
        const f = find(filters, { _id: (criterion.filter_id || criterion._id) });
        if (f) {
          criterion.filter = f;
          items.push(criterion);
        }
        return items;
      }, []);
      const minCloseDate = minDate ? moment(minDate, 'YYYY-MM-DD') : null;
      const maxCloseDate = maxDate ? moment(maxDate, 'YYYY-MM-DD') : null;

      const newSearch = {
        ...searches[searchType], criteria: newCriteria, id, minCloseDate, maxCloseDate,
      };

      return {
        ...state,
        searches: {
          ...searches,
          [searchType]: newSearch,
        },
      };
    }
    case ADD_SAVED_SEARCH: {
      const { search, searchType } = action;
      const { searches } = state;
      const { savedSearches } = searches[searchType];
      const newSavedSearches = concat(savedSearches, search);
      return {
        ...state,
        searches: {
          ...searches,
          [searchType]: {
            ...searches[searchType],
            savedSearches: newSavedSearches,
          },
        },
      };
    }
    case UPDATE_SAVED_SEARCH: {
      const { search, searchType } = action;
      const { searches } = state;
      const { savedSearches, savedSearch } = searches[searchType];
      const newSavedSearches = [...savedSearches];
      const idx = findIndex(newSavedSearches, { _id: search._id });
      if (idx >= 0) {
        newSavedSearches[idx] = { ...newSavedSearches[idx], ...search };
      }
      const newSavedSearch = savedSearch && savedSearch._id === search._id ? { ...savedSearch, ...search } : savedSearch;
      return {
        ...state,
        searches: {
          ...searches,
          [searchType]: {
            ...searches[searchType],
            savedSearches: newSavedSearches,
            savedSearch: newSavedSearch,
          },
        },
      };
    }
    case VIEW_SAVED_SEARCH: {
      const { search, objects, searchType } = action;
      const { searches } = state;
      const { criteria } = search;
      const {
        savedSearches, filters, defaultQuery,
      } = searches[searchType];

      let newSavedSearches = savedSearches.map(savedSearch => ({ ...savedSearch, active: false }));
      const idx = findIndex(newSavedSearches, { _id: search._id });
      if (idx >= 0) {
        newSavedSearches[idx] = { ...newSavedSearches[idx], ...search };
      }
      const query = addFilterQuery(criteria, filters);
      newSavedSearches = sortBy(newSavedSearches, newSavedSearch => -1 * (newSavedSearch.last_used_date ? moment(newSavedSearch.last_used_date).unix() : 9999999999));
      const isDirty = checkDirty(query, defaultQuery);
      return {
        ...state,
        searches: {
          ...searches,
          [searchType]: {
            ...searches[searchType],
            savedSearches: newSavedSearches,
            query,
            objects,
            savedSearch: search,
            isDirty,
          },
        },
      };
    }
    case RESET_DEFAULT_SEARCH: {
      const { searchType } = action;
      const { searches } = state;
      const { defaultQuery, savedSearches } = searches[searchType];
      const newSavedSearches = savedSearches.map(savedSearch => ({ ...savedSearch, active: false }));
      const query = cloneDeep(defaultQuery);
      const isDirty = false;
      return {
        ...state,
        searches: {
          ...searches,
          [searchType]: {
            ...searches[searchType],
            savedSearches: newSavedSearches,
            query,
            savedSearch: null,
            isDirty,
          },
        },
      };
    }
    case DELETE_SAVED_SEARCH: {
      const { id, searchType } = action;
      const { searches } = state;
      const { savedSearches, savedSearch } = searches[searchType];
      const newSavedSearches = lodashFilter(savedSearches, s => s._id !== id);
      const newSavedSearch = savedSearch && savedSearch._id === id ? { ...savedSearch, $deleted: true } : savedSearch;

      return {
        ...state,
        searches: {
          ...searches,
          [searchType]: {
            ...state.searches[searchType],
            savedSearches: newSavedSearches,
            savedSearch: newSavedSearch,
          },
        },
      };
    }
    case UPDATE_CRITERIA: {
      const { ids, params, searchType } = action;
      const { searches } = state;
      const { query, defaultQuery } = searches[searchType];
      const newQuery = updateItemFromPath(query, ids, params);
      const isDirty = checkDirty(newQuery, defaultQuery);
      return {
        ...state,
        searches: {
          ...searches,
          [searchType]: {
            ...searches[searchType],
            query: newQuery,
            isDirty,
          },
        },
      };
    }
    case DELETE_CRITERIA: {
      const { ids, searchType } = action;
      const { searches } = state;
      const { query, defaultQuery } = searches[searchType];
      const newQuery = removeItemFromPath(query, ids);
      const isDirty = checkDirty(newQuery, defaultQuery);
      return {
        ...state,
        searches: {
          ...searches,
          [searchType]: {
            ...searches[searchType],
            query: newQuery,
            isDirty,
          },
        },
      };
    }
    case ADD_CRITERIA: {
      const {
        id, parentQuery, params, searchType, filterId,
        filterInfo,
      } = action;
      const { searches } = state;
      const { query, defaultQuery } = searches[searchType];

      let { filters } = searches[searchType];
      if (filterInfo) {
        const existent = find(filters, { _id: filterInfo._id });
        if (!existent) {
          filters = [...filters, filterInfo];
        }
      }

      const criterion = { type: 'include', ...params, _id: id };
      if (!isArray(criterion.values)) {
        criterion.values = [];
      }
      if (criterion.type !== 'groupAND' && criterion.type !== 'groupOR') {
        const filter = find(filters, { _id: filterId });
        if (filter) {
          criterion.filter = filter;
          criterion.filter_id = filter._id;
          if (filter.type === 'range') {
            const minVal = first(filter.values);
            const maxVal = last(filter.values);
            if (!isArray(criterion.values)) {
              criterion.values = [];
            }
            if (criterion.values.length === 0) {
              criterion.values = [minVal, maxVal];
            } else if (criterion.values.length === 1) {
              criterion.values.push(maxVal);
            }
          } else if (filter.type === 'daterange') {
            criterion.values = searchType === 'pipeline' ? 'following-12-months' : 'last-12-months';
          } else if (filter.is_boolean) {
            criterion.values = [true];
          }
        }
      }
      // find root in tree
      const root = parentQuery ? findAndInsert(query, { _id: parentQuery._id }, criterion) : parentQuery;
      const isDirty = checkDirty(root, defaultQuery);

      
      if (root) {
        return {
          ...state,
          searches: {
            ...searches,
            [searchType]: {
              ...searches[searchType],
              query: root,
              isDirty,
              filters,
            },
          },
        };
      }
      return {
        ...state,
        searches: {
          ...searches,
          [searchType]: {
            ...searches[searchType],
            isDirty,
            filters,
          },
        },
      };
    }
    case LOAD_CRITERIA_FILTER_DETAIL: {
      const { id, options, searchType } = action;
      const { searches } = state;
      const { criteria, filters } = searches[searchType];
      const idx = findIndex(filters, { _id: id });
      const newFilters = [...filters];
      if (idx >= 0) {
        newFilters[idx] = { ...newFilters[idx], values: [...(newFilters[idx].values || []), ...options], valueLoaded: true };
        const newCriteria = reduce(criteria, (items, criterion) => {
          if (criterion.filter && criterion.filter._id === id) {
            items.push({ ...criterion, filter: newFilters[idx] });
          } else {
            items.push(criterion);
          }
          return items;
        }, []);
        return {
          ...state,
          searches: {
            ...searches,
            [searchType]: {
              ...searches[searchType],
              filters: newFilters,
              criteria: newCriteria,
            },
          },
        };
      }
      return state;
    }
    default:
      return state;
  }
};


function _getSearch(searchType, state) {
  const s = state.searchForm.searches ? state.searchForm.searches[searchType] || empty : empty;
  return s;
}

export const getSearchFilters = (searchType, state) => {
  const s = _getSearch(searchType, state);
  return s.filters;
};
export const getSearchCriteria = state => state.searchForm.criteria; // remove this
export const getSearchQuery = (searchType, state) => {
  const s = _getSearch(searchType, state);
  return s.query || {};
};
export const getSearchDefaultQuery = (searchType, state) => {
  const s = _getSearch(searchType, state);
  return s.defaultQuery || {};
};
export const getSearchObjects = (searchType, state) => {
  const s = _getSearch(searchType, state);
  return s.objects;
};
export const getSearchId = (searchType, state) => {
  const s = _getSearch(searchType, state);
  return s.id;
};
export const getFilterStatsConditions = (searchType, state) => {
  const s = _getSearch(searchType, state);
  return s.statsConditions;
};
export const getLoading = (searchType, state) => {
  const s = _getSearch(searchType, state);
  return s && s.status === 'loading';
};
export const getLoaded = (searchType, state) => {
  const s = _getSearch(searchType, state);
  return s && s.status === 'loaded';
};
export const getHasErrors = (searchType, state) => {
  const s = _getSearch(searchType, state);
  return s && s.status === 'error';
};
export const getIsDirty = (searchType, state) => {
  const s = _getSearch(searchType, state);
  return s.isDirty;
};
export const getSavedSearches = (searchType, state) => {
  const s = _getSearch(searchType, state);
  return s.savedSearches;
};
export const getSavedSearch = (searchType, state) => {
  const s = _getSearch(searchType, state);
  return s.savedSearch;
};

export const getCloseDateRange = (searchType, state) => {
  const s = _getSearch(searchType, state);
  return [s.minCloseDate, s.maxCloseDate];
};

export const getForecastCategoryFilter = state => state.searchForm.forecastCategoryFilter;
export const getCloseDateFilter = state => state.searchForm.closeDateFilter;

export const getHasNoDecisionSetup = state => state.searchForm.hasNoDecisionSetup;

export const getHasDealTypeSetup = state => state.searchForm.hasDealTypeSetup;

export const getAccountIndustryFilter = state => state.searchForm.accountIndustryFilter;

export const getErrors = (searchType, state) => {
  const s = _getSearch(searchType, state);
  return s.errors;
};

// Export Reducer
export default SearchFormReducer;
